import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Loader2, KanbanSquare, Search, Phone, Clock, Bot, User, AlertTriangle, RotateCcw, DollarSign,
} from "lucide-react";
import RevenueOutcomeDialog from "./RevenueOutcomeDialog";

const STAGE_HEX = { sky: "#2C6480", amber: "#B7791F", blue: "#2563EB", green: "#2F7D5A", orange: "#C2620F", purple: "#7C3AED", gray: "#6B7280" };
const LANES = [
  { value: "sell_home", label: "Selling the Home" }, { value: "senior_placement", label: "Senior Living" },
  { value: "home_safety", label: "Keep Mama Home" }, { value: "move_into_my_home", label: "Move Mama In" },
  { value: "downsizing", label: "Downsizing" }, { value: "home_repairs", label: "Home Repairs" },
  { value: "partner_referral", label: "Partner Referral" },
];
const URGENCIES = ["immediate", "high", "medium", "low", "planning"];
const pretty = (s) => String(s == null ? "" : s).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const nameOf = (l) => l.name || "Lead";

export default function CrmPipeline() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ q: "", service_lane: "all", urgency: "all" });
  const [rev, setRev] = useState(null); // { leadId, defaults, contextLabel }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { q: f.q || undefined, service_lane: f.service_lane === "all" ? undefined : f.service_lane, urgency: f.urgency === "all" ? undefined : f.urgency };
      const { data } = await api.get("/crm/pipeline", { params });
      setData(data); setStages(data.stages);
    } catch { toast.error("Could not load pipeline."); }
    finally { setLoading(false); }
  }, [f]);
  useEffect(() => { load(); }, [load]);

  const moveStage = async (leadId, stage) => {
    try { await api.post(`/crm/leads/${leadId}/stage`, { stage, reason: "Moved on board" }); toast.success("Stage updated."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
  };

  return (
    <div className="pb-10" data-testid="crm-pipeline">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2">
          <KanbanSquare size={22} className="text-[#1C4358]" />
          <h1 className="text-2xl font-display font-bold text-[#1C4358]">Pipeline Board</h1>
          {data && <span className="text-[13px] text-[#4B5563]">{data.total} leads</span>}
        </div>
        <Button variant="outline" size="sm" onClick={load} className="border-[#D6E0E7] text-[#1C4358] gap-1"><RotateCcw size={14} /> Refresh</Button>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative"><Search size={15} className="absolute left-2.5 top-2.5 text-[#4B5563]" /><Input data-testid="crm-search" value={f.q} onChange={(e) => setF((p) => ({ ...p, q: e.target.value }))} placeholder="Search name / phone / email" className="pl-8 h-9 w-64 text-[14px] rounded-lg border-[#1C4358]/20" /></div>
        <Select value={f.service_lane} onValueChange={(v) => setF((p) => ({ ...p, service_lane: v }))}><SelectTrigger data-testid="crm-filter-lane" className="w-[170px] h-9 text-[13px]"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All service lanes</SelectItem>{LANES.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent></Select>
        <Select value={f.urgency} onValueChange={(v) => setF((p) => ({ ...p, urgency: v }))}><SelectTrigger data-testid="crm-filter-urgency" className="w-[140px] h-9 text-[13px]"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All urgency</SelectItem>{URGENCIES.map((u) => <SelectItem key={u} value={u}>{pretty(u)}</SelectItem>)}</SelectContent></Select>
      </div>

      {loading && !data ? <div className="flex justify-center py-24"><Loader2 className="animate-spin text-[#1C4358]" size={30} /></div> : (
        <div className="overflow-x-auto pb-4">
          <div className="flex gap-3" style={{ minWidth: "max-content" }}>
            {stages.map((st) => {
              const hex = STAGE_HEX[st.color] || "#6B7280";
              const cards = data.columns[st.key] || [];
              return (
                <div key={st.key} className="w-[270px] shrink-0" data-testid={`crm-col-${st.key}`}>
                  <div className="flex items-center justify-between px-1 mb-2 sticky top-0">
                    <div className="flex items-center gap-1.5 text-[13px] font-bold text-[#1C4358]"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: hex }} />{st.label}</div>
                    <span className="text-[11px] font-semibold text-white rounded-full px-1.5" style={{ backgroundColor: hex }}>{data.counts[st.key]}</span>
                  </div>
                  <div className="space-y-2 rounded-xl bg-[#F5F8FA] p-2 min-h-[120px]">
                    {cards.length === 0 ? <p className="text-[12px] text-[#9AA5B1] text-center py-4">—</p> :
                      cards.map((l) => (
                        <div key={l.id} data-testid={`crm-card-${l.id}`} className="rounded-lg border border-[#E6EDF2] bg-white p-2.5 hover:border-[#1C4358]/40 transition-colors cursor-pointer"
                          onClick={() => navigate(`/crm/leads/${l.id}`)}>
                          <div className="flex items-center justify-between gap-1">
                            <span className="font-semibold text-[#1C4358] text-[14px] truncate">{nameOf(l)}</span>
                            {l.follow_up_missed && <AlertTriangle size={13} className="text-[#9C1E22] shrink-0" title="Missed follow-up" />}
                          </div>
                          <div className="text-[12px] text-[#4B5563] mt-0.5 truncate">{pretty(l.service_interest) || l.matched_service_lane || "—"}{l.phone ? ` · ${l.phone}` : ""}</div>
                          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                            {l.urgency_level && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: l.urgency_level === "immediate" ? "rgba(156,30,34,0.12)" : "rgba(28,67,88,0.08)", color: l.urgency_level === "immediate" ? "#9C1E22" : "#1C4358" }}>{pretty(l.urgency_level)}</span>}
                            <span className="text-[10px] px-1.5 py-0.5 rounded flex items-center gap-0.5 text-[#4B5563] bg-[#EEF3F6]">{l.pipeline_set_by === "system" ? <Bot size={9} /> : <User size={9} />}{l.pipeline_set_by || "—"}</span>
                            {l.next_follow_up_date && <span className="text-[10px] px-1.5 py-0.5 rounded flex items-center gap-0.5 text-[#B7791F] bg-[rgba(183,121,31,0.12)]"><Clock size={9} />{(l.next_follow_up_date || "").slice(5, 10)}</span>}
                          </div>
                          <Select value={l.pipeline_stage} onValueChange={(v) => moveStage(l.id, v)}>
                            <SelectTrigger data-testid={`crm-card-stage-${l.id}`} onClick={(e) => e.stopPropagation()} className="h-7 mt-2 text-[11px] border-[#E6EDF2]"><SelectValue /></SelectTrigger>
                            <SelectContent>{stages.map((s2) => <SelectItem key={s2.key} value={s2.key} className="text-[12px]">{s2.label}</SelectItem>)}</SelectContent>
                          </Select>
                          {l.pipeline_stage === "closed_won" && (
                            <button data-testid={`crm-card-revenue-${l.id}`} onClick={(e) => { e.stopPropagation(); setRev({ leadId: l.id, contextLabel: nameOf(l), defaults: { name: nameOf(l), service_lane: l.service_interest, actual_value: l.deal_value } }); }}
                              className="mt-2 w-full flex items-center justify-center gap-1 rounded-md h-7 text-[11px] font-semibold text-white bg-[#2F7D5A] hover:bg-[#276848] transition-colors"><DollarSign size={12} /> Add revenue outcome</button>
                          )}
                        </div>
                      ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {rev && (
        <RevenueOutcomeDialog open={!!rev} onOpenChange={(v) => !v && setRev(null)}
          leadId={rev.leadId} defaults={rev.defaults} contextLabel={rev.contextLabel}
          onSaved={() => { setRev(null); load(); }} />
      )}
    </div>
  );
}
